<template>
  <div class="dropzone" @drop.prevent="onDrop" @dragover.prevent="onDragOver">
    <div class="container">
      <div class="inner_container">
        <div class="drop_icon">
          <span class="material-symbols-outlined">download</span>
        </div>

        <!-- single form for the file input -->
        <form @submit.prevent="noop">
          <input
            id="file-selector"
            ref="fileInput"
            type="file"
            style="display: none"
            :accept="Array.from(allowedMimeTypes).join(',')"
            :multiple="!singleFile"
            @change="onFileInput"
          />
          <label class="button primary" for="file-selector">
            {{ $t("upload.browseFiles.title") }}
          </label>
        </form>

        <div class="info">{{ $t("upload.dropTitle") }}</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, Ref } from "vue";

const DEFAULT_ALLOWED = new Set<string>([
  "application/pdf",
  "video/mp4",
  "audio/*",
  ".docx",
  ".pptx",
  ".xlsx",
  ".png",
  "image/jpeg",
]);

const props = defineProps<{
  mimeTypes?: string[];
  singleFile: boolean;
}>();

const emit = defineEmits<{
  (e: "submit", files: File[]): void;
  (e: "error", message: string): void;
}>();

const fileInput: Ref<HTMLInputElement | null> = ref(null);
const allowedMimeTypes = props.mimeTypes
  ? new Set(props.mimeTypes)
  : DEFAULT_ALLOWED;

const isAllowed = (file: File) => {
  if (allowedMimeTypes.has(file.type)) return true;
  const lower = file.name.toLowerCase();
  for (const t of allowedMimeTypes) {
    // Wildcard MIME entries like "audio/*" match by type prefix.
    if (t.endsWith("/*") && file.type.startsWith(t.slice(0, -1))) return true;
    if (t.startsWith(".") && lower.endsWith(t)) return true;
  }
  return false;
};

const normalize = (list: FileList | null | undefined): File[] => {
  if (!list) return [];
  const filtered = Array.from(list).filter(isAllowed);
  return props.singleFile ? filtered.slice(0, 1) : filtered;
};

const onFileInput = () => {
  const files = normalize(fileInput.value?.files);
  if (files.length === 0) {
    emit("error", "No acceptable files selected.");
    return;
  }
  emit("submit", files);
};

const onDragOver = (e: DragEvent) => {
  const count = e.dataTransfer?.items?.length ?? 0;
  e.dataTransfer!.dropEffect = props.singleFile && count > 1 ? "none" : "copy";
};

const onDrop = (e: DragEvent) => {
  const dropped = e.dataTransfer?.files;
  if (!dropped) return;

  if (props.singleFile && dropped.length > 1) {
    emit("error", "Only one file allowed.");
    return;
  }

  const files = normalize(dropped);
  if (files.length === 0) {
    emit("error", "No acceptable files dropped.");
    return;
  }
  emit("submit", files);
};

const noop = () => {};
</script>

<style scoped lang="scss">
.dropzone {
  position: absolute;
  inset: 0;
  border: 1px dashed var(--classic-text-secondary, rgba(25, 23, 17, 0.6));
}

.container {
  display: flex;
  height: 100%;
  justify-content: center;
  align-items: center;
}

.inner_container {
  display: block;
}

.drop_icon span {
  font-size: 6em;
  color: var(--classic-text-muted, rgba(25, 23, 17, 0.2));
}

.button {
  font-size: 1em;
  margin: 25px 0 5px 0;
}

.info {
  display: block;
  font-size: 0.9em;
  margin-top: 0.5em;
}
</style>
